import React, { useState, useCallback } from 'react';
import { AmbientSound } from '../types';
import { AudioEngine } from '../hooks/useAudioEngine';
import './AmbientPanel.css';

interface Props {
  sounds: AmbientSound[];
  audioEngine: AudioEngine;
  disabled?: boolean;
}

interface SoundState {
  active: boolean;
  volume: number;
}

export function AmbientPanel({ sounds, audioEngine, disabled = false }: Props) {
  const [soundStates, setSoundStates] = useState<Record<string, SoundState>>(() => {
    const init: Record<string, SoundState> = {};
    sounds.forEach(s => { init[s.id] = { active: false, volume: 0.5 }; });
    return init;
  });

  const toggleSound = useCallback((sound: AmbientSound) => {
    if (disabled) return;
    setSoundStates(prev => {
      const current = prev[sound.id] ?? { active: false, volume: 0.5 };
      const newActive = !current.active;
      if (newActive) {
        audioEngine.playAmbient(sound.id, sound.file, current.volume);
      } else {
        audioEngine.stopAmbient(sound.id);
      }
      return { ...prev, [sound.id]: { ...current, active: newActive } };
    });
  }, [audioEngine, disabled]);

  const handleVolumeChange = useCallback((sound: AmbientSound, vol: number) => {
    setSoundStates(prev => {
      const current = prev[sound.id] ?? { active: false, volume: 0.5 };
      if (current.active) {
        audioEngine.setAmbientVolume(sound.id, vol);
      }
      return { ...prev, [sound.id]: { ...current, volume: vol } };
    });
  }, [audioEngine]);

  return (
    <div className={`ambient-panel ${disabled ? 'disabled' : ''}`}>
      {sounds.map(sound => {
        const state = soundStates[sound.id] ?? { active: false, volume: 0.5 };
        return (
          <div key={sound.id} className={`ambient-item ${state.active ? 'active' : ''}`}>
            <button
              className="ambient-toggle"
              onClick={() => toggleSound(sound)}
              aria-pressed={state.active}
              disabled={disabled}
            >
              <span className="ambient-icon">{sound.icon}</span>
              <span className="ambient-label">{sound.label}</span>
              {state.active && <span className="ambient-active-dot" />}
            </button>
            {state.active && (
              <div className="ambient-volume-row">
                <span className="ambient-vol-icon">🔊</span>
                <input
                  type="range"
                  className="volume-slider ambient-vol-slider"
                  min={0} max={1} step={0.01}
                  value={state.volume}
                  onChange={e => handleVolumeChange(sound, Number(e.target.value))}
                  disabled={disabled}
                />
                <span className="volume-pct">{Math.round(state.volume * 100)}%</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
